import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet, Router, NavigationEnd, RouterModule } from '@angular/router';
import { filter } from 'rxjs/operators';
import { WebsocketService } from "./services/websocket.service";
import { KeycloakService } from "keycloak-angular";
import { ToastrService } from "ngx-toastr";
import { IconComponent } from './components/shared/icon/icon.component';
import { ThemeService } from './services/theme.service';
import { PageTransitionComponent } from './components/shared/page-transition/page-transition.component';
import { AnimationService } from './services/animation.service';
import { IWSMsg } from "./model/ws-mgs";
import { Location } from '@angular/common';
import { environment } from '../environments/environment';
import { AuthService } from './services/auth.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Lang, LANGS, LocaleService } from './services/locale.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterModule,
    IconComponent,
    PageTransitionComponent,
    TranslatePipe
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'RSSFront';
  wsMgs: IWSMsg | undefined;
  username: string | undefined;
  environment = environment;
  public readonly isConnected$ = this.websocketService.isConnected$;
  private previousUrl: string = '';
  version: string = '0.0.0';
  profileMenuOpen = false;
  langs = LANGS;

  constructor(
    private websocketService: WebsocketService,
    private keycloakService: KeycloakService,
    private toastr: ToastrService,
    private router: Router,
    private location: Location,
    private animationService: AnimationService,
    private auth: AuthService,
    private translate: TranslateService,
    public themeService: ThemeService,
    public localeService: LocaleService
  ) {
    // Handle back button
    this.location.subscribe(() => {
      this.animationService.triggerLeaveAnimation();
      setTimeout(() => {
        this.animationService.triggerEnterAnimation();
      }, 300); // Match the animation duration
    });
  }

  async ngOnInit(): Promise<void> {
    console.log('App Component initializing...');
    
    if (await this.keycloakService.isLoggedIn()) {
      //console.log('User is logged in');
      const userProfile = await this.keycloakService.loadUserProfile();
      this.username = userProfile.username;
      this.auth.setUserProfile(userProfile);
      const adminRole = await this.keycloakService.isUserInRole('admin');
      //console.log('admin role', adminRole);
      this.auth.setAdmin(adminRole);
      // Connect websocket after confirming login
      //console.log('Initializing WebSocket connection...');
      this.websocketService.connect();
      
      this.websocketService.messages$.subscribe(
        msg => {
          // console.log('WebSocket message received:', msg);
          this.wsMgs = msg;
          this.processMessage(msg);
        },
        error => {
          console.error('WebSocket error:', error);
          this.toastr.error(this.translate.instant('ws.connection_error'));
        },
        () => {
          console.log('WebSocket connection completed/closed');
        }
      );
    } else {
      console.log('User is not logged in');
    }

    // Handle navigation events
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      // Determine navigation direction
      const isForward = !this.previousUrl || this.previousUrl.length < event.url.length;
      
      // Trigger appropriate animation
      if (isForward) {
        this.animationService.triggerEnterAnimation();
      } else {
        this.animationService.triggerLeaveAnimation();
        setTimeout(() => {
          this.animationService.triggerEnterAnimation();
        }, 300);
      }
      
      // Store current URL for next navigation
      this.previousUrl = event.url;
    });
  }

  @HostListener('document:click')
  closeProfileMenu(): void {
    this.profileMenuOpen = false;
  }

  toggleProfileMenu(event: Event): void {
    event.stopPropagation();
    this.profileMenuOpen = !this.profileMenuOpen;
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
    this.profileMenuOpen = false;
  }

  async logout(): Promise<void> {
    this.profileMenuOpen = false;
    await this.keycloakService.logout(window.location.origin);
  }

  /** Apply locally at once, then persist to the profile so other devices follow. */
  async setLang(lang: Lang): Promise<void> {
    if (lang === this.localeService.current) return;
    await this.localeService.set(lang);
    this.websocketService.sendMessage({ type: 'SETUSERDATA', payload: { lang } });
  }

  /**
   * Server-sent toast text. The backend now sends a `code` (plus `params`); `message`
   * stays as the fallback for codes this build doesn't know and for older servers.
   */
  private toastText(payload: any): string {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    if (payload.code) {
      const key = `ws.msg.${payload.code}`;
      const text = this.translate.instant(key, payload.params ?? {});
      if (text !== key) return text;
    }
    return payload.message ?? '';
  }

  processMessage(message: any): void {
    //this.wsMgs = message;
    const payload = message.payload;
    if (message.type === 'UPDATE') {
      // processed in controller-details
    } else if (message.type === 'AUTHORIZED') {
      this.websocketService.sendMessage({
            type: 'USERDATA',
            payload: { }
          });
      this.websocketService.getUserDevices();
    } else if (message.type === 'USERDEVICES') {      
      //this.websocketService.setControllers(payload);
      //this.dataService.setControllers(payload); // temp
    } else if (message.type === 'SUCCESS') {
      this.toastr.success(this.toastText(payload));
    } else if (message.type === 'ERROR') {
      this.toastr.error(this.toastText(payload));
    } else if (message.type === 'WARNING') {
      this.toastr.warning(this.toastText(payload));
    } else if (message.type === 'LINKOK') {
      this.toastr.success(this.translate.instant('ws.msg.link_ok', { mac: payload }));
    } else if (message.type === 'LINK') {
      this.toastr.info(this.translate.instant('ws.msg.link_press_button'), '', {
        "timeOut": 60000,
        progressBar: true,
        closeButton: true
      }).onHidden
        .subscribe(() => {
          //console.log(1)
          this.websocketService.sendMessage({
            type: 'REQUESTFORLINK',
            payload: { event: 'linkRequestTimeout' }
          });
        });
    } else if (message.type === 'VERSION') {
      this.version = payload;
    } else if (message.type === 'USERDATA') {
      this.auth.setUserData(payload);
      // The profile is the source of truth; the pre-render guess was only a stand-in.
      this.localeService.applyFromServer(payload?.lang);
    }
    //console.log('processMessage', message);
  }

  updateControllerDetails(mac: string): void {
    const detailsComponent = (window as any).currentControllerDetails;
    if (detailsComponent?.controller?.mac === mac) {
      detailsComponent.refreshView();
    }
  }

  linkController(): void {
    this.router.navigate(['/linkcontroller/00000000']);
  }

  firmware(): void {
    this.router.navigate(['/firmware']);
  }

  telegram(): void {
    this.profileMenuOpen = false;
    this.router.navigate(['/telegram']);
  }
}


